import { useMemo } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { History, Pencil, Plus, Trash2, Undo2, X, type LucideIcon } from 'lucide-react';
import {
  taxonomyFieldTemplateChangeLogQueryOptions,
  type TaxonomyFieldTemplate,
  type TaxonomyFieldTemplateChangeLogEntry,
} from '@/entities/taxonomy-field-template';
import type { TaxonomyChangeAction } from '@/entities/taxonomy';

export interface FieldHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: TaxonomyFieldTemplate;
  currentUserId?: string;
  currentUserName?: string | null;
}

// Getters, not values: a resolved label would freeze at import.
const ACTION_META: Partial<Record<TaxonomyChangeAction, { label: string; icon: LucideIcon }>> = {
  created: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldHistoryDrawer.created');
    },
    icon: Plus,
  },
  updated: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldHistoryDrawer.edited');
    },
    icon: Pencil,
  },
  deleted: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldHistoryDrawer.deleted');
    },
    icon: Trash2,
  },
  restored: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldHistoryDrawer.restored');
    },
    icon: Undo2,
  },
};

/** Attributes worth showing a before→after for; the rest are noise in a trail. */
/* eslint-disable i18next/no-literal-string -- property names, not copy */
const TRACKED_KEYS = [
  'name',
  'description',
  'category',
  'group',
  'type',
  'level',
  'required',
  'visible',
  'unique',
  'format',
  'defaultValue',
  'validationPattern',
  'transform',
  'listRef',
  'values',
] as const;
/* eslint-enable i18next/no-literal-string */

function render(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value))
    return i18n.t('knowledge:taxonomy.fieldHistoryDrawer.optionCount', { count: value.length });
  if (typeof value === 'object') return i18n.t('knowledge:taxonomy.fieldHistoryDrawer.linkedList');
  return String(value);
}

/** The attributes that actually differ between two snapshots. */
function changedKeys(entry: TaxonomyFieldTemplateChangeLogEntry): string[] {
  if (!entry.before || !entry.after) return [];
  return TRACKED_KEYS.filter(
    (k) => JSON.stringify(entry.before?.[k]) !== JSON.stringify(entry.after?.[k]),
  );
}

/**
 * A field template's audit trail.
 *
 * NOT a version browser: templates are copy-on-add, so editing one changes
 * nothing that already exists and there is no past state for a schema to point
 * at. What matters is accountability — who changed the org's shared definition
 * and what it used to say — so each entry renders the attributes that actually
 * moved, before → after.
 */
export function FieldHistoryDrawer(props: FieldHistoryDrawerProps) {
  const { t } = useTranslation('knowledge');
  const { open, onOpenChange, template, currentUserId, currentUserName } = props;

  const query = useQuery({
    ...taxonomyFieldTemplateChangeLogQueryOptions(template.id),
    enabled: open,
  });
  const entries = useMemo(() => query.data ?? [], [query.data]);

  const actorLabel = (actorId: string) =>
    actorId === currentUserId
      ? (currentUserName ?? t('taxonomy.fieldHistoryDrawer.you'))
      : t('taxonomy.fieldHistoryDrawer.teamMember');

  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex h-full w-full flex-col bg-card sm:!max-w-[440px]">
        <DrawerHeader className="flex flex-row items-center justify-between border-b border-border-light">
          <DrawerTitle className="flex min-w-0 items-center gap-2 text-base">
            <History className="size-4 shrink-0" aria-hidden />
            <span className="truncate">
              {template.name} {t('taxonomy.fieldHistoryDrawer.history')}
            </span>
          </DrawerTitle>
          <DrawerClose aria-label={t('taxonomy.fieldHistoryDrawer.closeHistory')}>
            <X className="size-4" />
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : query.isError ? (
            // Without this, a 403 or a 500 on an audit trail reads as "nothing
            // has ever happened to this field" — the one answer a change log must
            // never give when it does not actually know.
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t('taxonomy.fieldHistoryDrawer.couldnTLoadHistory')}
            </p>
          ) : entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t('taxonomy.fieldHistoryDrawer.noChangesRecordedYet')}
            </p>
          ) : (
            <ol className="flex flex-col gap-4">
              {entries.map((entry) => {
                const meta = ACTION_META[entry.action] ?? { label: entry.action, icon: History };
                const Icon = meta.icon;
                const keys = changedKeys(entry);
                return (
                  <li key={entry.id} className="flex gap-3">
                    <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-surface text-muted-foreground">
                      <Icon className="size-3.5" aria-hidden />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="text-sm font-medium text-foreground">{meta.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {actorLabel(entry.actorId)} ·{' '}
                        <time
                          dateTime={entry.createdAt}
                          // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to `format()` rather than copy
                          title={format(new Date(entry.createdAt), 'PPpp')}
                        >
                          {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                        </time>
                      </span>
                      {keys.length > 0 && (
                        <ul className="mt-0.5 flex flex-col gap-0.5">
                          {keys.map((k) => (
                            <li key={k} className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">{k}</span>{' '}
                              <span className="line-through opacity-70">
                                {render(entry.before?.[k])}
                              </span>{' '}
                              → <span>{render(entry.after?.[k])}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

FieldHistoryDrawer.displayName = 'FieldHistoryDrawer';
